import Link from 'next/link';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { Button } from '@/components/buttons';
import { IconComponent } from '@/components/IconComponent';
import { ImageComponent } from '@/components/Image';
import {
  ProgressBar,
  ValuePosition,
} from '@/components/progressBars/LinearProgressBar';
import { ProgressBarMode } from '@/components/progressBars/LinearProgressBar/styled';
import { Skeleton } from '@/components/Skeleton';
import { Modals } from '@/helpers/modals';
import { useToggle } from '@/hooks/common';
import { useBonanzaPromo } from '@/modules/promotions';
import { useGetCommonStore, useModalsStore } from '@/store';
import { ScBonanzaPromoSection } from './styled';

export interface BonanzaPromoSectionProps {
  isMobile?: boolean;
  onLinkClick?: () => void;
}

export const BonanzaPromoSection: FC<BonanzaPromoSectionProps> = ({
  isMobile,
  onLinkClick,
}) => {
  const { t } = useTranslation();

  const [isFullInfoOpen, toggleFullInfo] = useToggle();

  const useCommonStore = useGetCommonStore();
  const open = useModalsStore.use.open();

  const {
    isAuthenticated,
    bonanzaReward,
    bonanzaPromoMenuData,
    bonanzaProgressPercent,
    pendingBonanzaPromoMenuData,
  } = useCommonStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      bonanzaReward: state.bonanzaReward,
      bonanzaPromoMenuData: state.bonanzaPromoMenuData,
      bonanzaProgressPercent: state.bonanzaProgressPercent,
      pendingBonanzaPromoMenuData: state.pendingBonanzaPromoMenuData,
    }))
  );

  const {
    isLockedPromo,
    isBonusIssued,
    nextIterationStartDay,
    characterByStatus,
    handleAcknowledgeBonus,
    handleNavigateToBonanza,
  } = useBonanzaPromo();

  const levelInfo = useMemo(() => {
    if (isLockedPromo) {
      const returnText = nextIterationStartDay
        ? bonanzaPromoMenuData?.menu.returnToStartFresh?.replace(
            '{{day}}',
            nextIterationStartDay
          )
        : bonanzaPromoMenuData?.menu.returnToStartFresh;

      return (
        <div className="text">
          <span>
            <b>{returnText}</b>
          </span>
        </div>
      );
    }

    return (
      <div className="text">
        <span>
          <b>{bonanzaPromoMenuData?.menu.yourProgress}:</b>
          {bonanzaProgressPercent}%
        </span>
        <span>
          <b>
            {isBonusIssued
              ? t('common:bonanzaPromo.bonusReceived')
              : bonanzaPromoMenuData?.menu.yourReward}
            :
          </b>
          {bonanzaReward}
        </span>
      </div>
    );
  }, [
    t,
    bonanzaPromoMenuData,
    bonanzaProgressPercent,
    bonanzaReward,
    isBonusIssued,
    isLockedPromo,
    nextIterationStartDay,
  ]);

  if (pendingBonanzaPromoMenuData)
    return <Skeleton.Rect height={isMobile ? 84 : 136} />;

  if (!(characterByStatus && bonanzaPromoMenuData)) return null;

  if (!isAuthenticated)
    return (
      <ScBonanzaPromoSection
        onClick={() => isMobile && toggleFullInfo()}
        showFullInfo={isFullInfoOpen}
      >
        <div className="info">
          <div className="level-info">
            <div className="unauth-text">{t('bonanzaPromo.loginToJoin')}</div>
            <div className="addons">
              <Link
                href="/bonanza"
                onClick={(e) => e.stopPropagation()}
                scroll={true}
              >
                <ImageComponent
                  alt="logo"
                  height={44}
                  objectFit="contain"
                  src={characterByStatus?.image?.url || ''}
                  width={44}
                />
              </Link>
              <IconComponent name="whiteArrowRight" size="18px" />
            </div>
          </div>
        </div>
        {isFullInfoOpen && (
          <Button
            className="loginBtn"
            navigation
            onClick={() => {
              onLinkClick && onLinkClick();
              open(Modals.Login);
            }}
          >
            {t('button.logIn')}
          </Button>
        )}
      </ScBonanzaPromoSection>
    );

  return (
    <ScBonanzaPromoSection
      onClick={() => isMobile && toggleFullInfo()}
      showFullInfo={isFullInfoOpen}
    >
      <div className="info">
        <div className="level-info">
          {levelInfo}
          {isMobile && (
            <div className="addons">
              <ImageComponent
                alt="logo"
                height={44}
                objectFit="contain"
                src={characterByStatus?.image?.url || ''}
                width={44}
              />
              <IconComponent name="whiteArrowRight" size="18px" />
            </div>
          )}
        </div>
      </div>

      {!(isLockedPromo || isBonusIssued) && (
        <ProgressBar
          borderRadius="24px"
          borderRadiusInner={'24px 0 0 24px'}
          height={24}
          mode={ProgressBarMode.PRIMARY_GRAY}
          showTo={false}
          showValue={ValuePosition.CENTER}
          value={bonanzaProgressPercent}
          withUnits
        />
      )}
      {isBonusIssued && (
        <Button onClick={handleAcknowledgeBonus} primary small wide>
          {t('common:gotIt')}
        </Button>
      )}

      {(!isMobile || isFullInfoOpen) && (
        <Button
          navigation
          onClick={() => {
            onLinkClick && onLinkClick();
            handleNavigateToBonanza();
          }}
        >
          {bonanzaPromoMenuData.pageTitle}
        </Button>
      )}
    </ScBonanzaPromoSection>
  );
};
